import type { Entity, World } from 'koota';
import { getContext } from '../context';
import { destroyBody } from '../physics/bodies';
import {
  ArmsRaceLastHit,
  ArmsRacePlayer,
  ArmsRaceState,
  Dead,
  Health,
  Held,
  HeldBy,
  PartyPlayer,
  Player,
  RoundPhase,
  RoundState,
  Transform,
  Weapon,
} from '../traits';
import { weaponByIndex, weaponIndex } from '../weapons/defs';
import { spawnWeapon } from '../weapons/systems';
import {
  ARMS_RACE_LADDER,
  ARMS_RACE_RINGOUT_TICKS,
  type ArmsRaceProgress,
  type DamageCredit,
} from './armsRaceConfig';
export {
  ARMS_RACE_LADDER,
  ARMS_RACE_RESPAWN_TICKS,
  type ArmsRaceProgress,
  type DamageCredit,
} from './armsRaceConfig';

function removeWeapon(world: World, weapon: Entity): void {
  destroyBody(world, weapon);
  weapon.destroy();
}

/** Keeps the current ladder weapon, replenishing empty magazines without a throw/pickup detour. */
export function equipArmsRaceWeapon(world: World, player: Entity, refill = false): void {
  const ctx = getContext(world),
    state = player.get(ArmsRacePlayer),
    position = player.get(Transform);
  if (
    ctx.settings.mode !== 'arms-race' ||
    !state ||
    player.has(Dead) ||
    (player.get(Health)?.hp ?? 0) <= 0 ||
    !position
  )
    return;
  const stage = ARMS_RACE_LADDER[state.stage]!;
  const expected = weaponIndex(stage.weaponId);
  let held: Entity | undefined;
  for (const weapon of [...world.query(Weapon, Held)]) {
    if (weapon.targetFor(HeldBy) !== player) continue;
    if (weapon.get(Weapon)!.defId === expected && !held) held = weapon;
    else removeWeapon(world, weapon);
  }
  if (!held) {
    held = spawnWeapon(world, stage.weaponId, position.x, position.y, false);
    held.add(Held(), HeldBy(player));
    ctx.bodies.get(held)?.setActive(false);
    ctx.fireCd.delete(player);
  }
  const weapon = held.get(Weapon)!;
  if (refill || weapon.ammo <= 0)
    held.set(Weapon, { ...weapon, ammo: weaponByIndex(expected).ammo });
}

/** Called at initial countdown and recovery; existing progress is deliberately preserved. */
export function startArmsRace(world: World): void {
  const ctx = getContext(world);
  if (ctx.settings.mode !== 'arms-race') return;
  if (!world.get(ArmsRaceState)) world.add(ArmsRaceState());
  for (const player of ctx.players) {
    if (!player.has(ArmsRacePlayer)) player.add(ArmsRacePlayer());
    if (!player.has(PartyPlayer)) player.add(PartyPlayer());
    if (!player.has(ArmsRaceLastHit)) player.add(ArmsRaceLastHit());
  }
  maintainArmsRaceLoadouts(world);
}

export function maintainArmsRaceLoadouts(world: World): void {
  const ctx = getContext(world);
  if (ctx.settings.mode !== 'arms-race') return;
  // Authored pickups, disarmed guns and debug drops cannot skip or pollute the ladder.
  for (const weapon of [...world.query(Weapon)]) {
    const holder = weapon.has(Held) ? weapon.targetFor(HeldBy) : undefined;
    if (!holder || !ctx.players.includes(holder) || holder.has(Dead)) removeWeapon(world, weapon);
  }
  for (const player of ctx.players) equipArmsRaceWeapon(world, player);
}

/** Called only on a transition from positive health to a lethal enemy hit. */
export function creditArmsRaceKill(
  world: World,
  source: number,
  target: Entity,
  credit?: DamageCredit,
): void {
  const ctx = getContext(world),
    race = world.get(ArmsRaceState);
  if (
    ctx.settings.mode !== 'arms-race' ||
    !race ||
    race.winner >= 0 ||
    !credit ||
    source === target ||
    source < 0
  )
    return;
  if (world.get(RoundState)?.phase !== RoundPhase.Fighting) return;
  const attacker = source as Entity;
  if (!ctx.players.includes(attacker) || !ctx.players.includes(target)) return;
  const state = attacker.get(ArmsRacePlayer);
  if (!state || credit.stage !== state.stage) return;
  const stage = ARMS_RACE_LADDER[state.stage]!;
  if (credit.weaponDef !== weaponIndex(stage.weaponId)) return;
  state.totalKills++;
  state.stageKills++;
  if (state.stageKills >= stage.killsRequired) {
    if (state.stage === ARMS_RACE_LADDER.length - 1) {
      world.set(ArmsRaceState, { winner: attacker.get(Player)!.slot });
    } else {
      state.stage++;
      state.stageKills = 0;
    }
  }
  attacker.set(ArmsRacePlayer, state);
  equipArmsRaceWeapon(world, attacker, true);
}

/** A short, resettable attribution window connects damaging knockback to the resulting fall. */
export function markArmsRaceHit(
  world: World,
  target: Entity,
  source: number,
  credit?: DamageCredit,
): void {
  const ctx = getContext(world);
  if (ctx.settings.mode !== 'arms-race' || !target.has(ArmsRaceLastHit)) return;
  const attacker = ctx.players.find((player) => player === source && player !== target);
  target.set(
    ArmsRaceLastHit,
    attacker && credit
      ? {
          sourceSlot: attacker.get(Player)!.slot,
          weaponDef: credit.weaponDef,
          stage: credit.stage,
          tick: ctx.tick,
        }
      : { sourceSlot: -1, weaponDef: -1, stage: -1, tick: -1 },
  );
}

export function creditArmsRaceRingOut(world: World, target: Entity): number {
  const ctx = getContext(world),
    hit = target.get(ArmsRaceLastHit);
  if (
    ctx.settings.mode !== 'arms-race' ||
    !hit ||
    hit.tick < 0 ||
    ctx.tick - hit.tick > ARMS_RACE_RINGOUT_TICKS
  )
    return -1;
  const attacker = ctx.players.find((player) => player.get(Player)!.slot === hit.sourceSlot);
  if (!attacker) return -1;
  creditArmsRaceKill(world, attacker, target, { weaponDef: hit.weaponDef, stage: hit.stage });
  return attacker;
}

export function captureArmsRaceProgress(world: World): ArmsRaceProgress | undefined {
  const ctx = getContext(world);
  if (ctx.settings.mode !== 'arms-race') return undefined;
  return {
    winner: world.get(ArmsRaceState)?.winner ?? -1,
    players: ctx.players.map((player) => ({
      slot: player.get(Player)!.slot,
      ...(player.get(ArmsRacePlayer) ?? { stage: 0, stageKills: 0, totalKills: 0 }),
    })),
  };
}

export function restoreArmsRaceProgress(world: World, progress: ArmsRaceProgress): void {
  if (getContext(world).settings.mode !== 'arms-race') return;
  startArmsRace(world);
  world.set(ArmsRaceState, { winner: progress.winner });
  for (const saved of progress.players) {
    const player = getContext(world).players.find(
      (fighter) => fighter.get(Player)!.slot === saved.slot,
    );
    if (!player) continue;
    player.set(ArmsRacePlayer, {
      stage: saved.stage,
      stageKills: saved.stageKills,
      totalKills: saved.totalKills,
    });
    equipArmsRaceWeapon(world, player, true);
  }
}

export function armsRaceStatus(world: World) {
  const progress = captureArmsRaceProgress(world);
  if (!progress) return null;
  const ctx = getContext(world);
  return {
    winner: progress.winner,
    players: progress.players.map((player) => ({
      ...player,
      weaponId: ARMS_RACE_LADDER[player.stage]!.weaponId,
      killsRequired: ARMS_RACE_LADDER[player.stage]!.killsRequired,
      nextWeaponId: ARMS_RACE_LADDER[player.stage + 1]?.weaponId ?? null,
      respawnTicks:
        ctx.players.find((fighter) => fighter.get(Player)!.slot === player.slot)?.get(PartyPlayer)
          ?.respawnTicks ?? 0,
    })),
  };
}
